import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from 'sonner';
import {
  AlertTriangle, ShieldCheck, Trash2, RefreshCw, History, Settings,
  CheckCircle2, XCircle, Mail, Loader2, FileSearch,
} from 'lucide-react';

const fmtCLP = (n) => (Number(n) || 0).toLocaleString('es-CL');
const RIESGO_META = {
  alto: { txt: 'ALTO', cls: 'bg-red-100 text-red-800 border-red-300', icon: '🚨' },
  medio: { txt: 'MEDIO', cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: '⚠️' },
  bajo: { txt: 'BAJO', cls: 'bg-gray-100 text-gray-700 border-gray-300', icon: '·' },
};

export default function DuplicadosFacturasPage() {
  const [tab, setTab] = useState('pendientes');
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({ duplicados: [], total_pendientes: 0, por_regla: {}, monto_riesgo_alto: 0 });
  const [decisiones, setDecisiones] = useState([]);
  const [historicos, setHistoricos] = useState({ historicos: [], monto_a_recuperar: 0 });
  const [cfg, setCfg] = useState({
    enabled: false, hora: '08:30', dias_ventana: 15, to: [], cc: [], groups_to: [], groups_cc: [], incluir_pagadas_historico: false,
  });
  const [cfgDirty, setCfgDirty] = useState(false);

  // Grupos de email reutilizables
  const [groups, setGroups] = useState({});
  const [groupsDirty, setGroupsDirty] = useState(false);

  // Dialog de decisión
  const [decDlg, setDecDlg] = useState(null); // {dup, decision: 'duplicado'|'legitimo'}
  const [eliminarId, setEliminarId] = useState('');
  const [obsDec, setObsDec] = useState('');
  const [procesando, setProcesando] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d, h, c, g] = await Promise.all([
        api.get('/contabilidad/duplicados/preview'),
        api.get('/contabilidad/duplicados/decisiones'),
        api.get('/contabilidad/duplicados/historicos'),
        api.get('/contabilidad/duplicados/config'),
        api.get('/email-groups'),
      ]);
      setPending(p.data || {});
      setDecisiones(d.data?.decisiones || []);
      setHistoricos(h.data || {});
      setCfg({ ...cfg, ...c.data });
      setGroups(g.data?.groups || {});
      setCfgDirty(false);
      setGroupsDirty(false);
    } catch { toast.error('Error al cargar duplicados'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const openDecision = (dup, decision) => {
    setDecDlg({ dup, decision });
    setEliminarId(dup.bills?.[1]?.alegra_id || '');
    setObsDec('');
  };

  const confirmDecision = async () => {
    if (!decDlg) return;
    const { dup, decision } = decDlg;
    setProcesando(true);
    try {
      await api.post('/contabilidad/duplicados/decidir', {
        alegra_id_a: dup.bills[0].alegra_id,
        alegra_id_b: dup.bills[1].alegra_id,
        decision,
        eliminar_alegra_id: decision === 'duplicado' ? eliminarId : null,
        observacion: obsDec.trim(),
      });
      toast.success(decision === 'duplicado' ? 'Duplicado confirmado · factura eliminada' : 'Marcadas como legítimas');
      setDecDlg(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al decidir');
    } finally { setProcesando(false); }
  };

  const saveCfg = async () => {
    try {
      await api.put('/contabilidad/duplicados/config', cfg);
      toast.success('Configuración guardada');
      setCfgDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando config');
    }
  };
  const saveGroups = async () => {
    try {
      await api.put('/email-groups', { groups });
      toast.success('Grupos guardados');
      setGroupsDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando grupos');
    }
  };
  const toggleGroupOn = (field, key) => {
    const arr = cfg[field] || [];
    const next = arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];
    setCfg({ ...cfg, [field]: next });
    setCfgDirty(true);
  };
  const updateGroupEmails = (key, raw) => {
    const emails = raw.split(',').map((s) => s.trim()).filter(Boolean);
    setGroups({ ...groups, [key]: { ...(groups[key] || {}), label: groups[key]?.label || key, emails } });
    setGroupsDirty(true);
  };
  const updateGroupLabel = (key, label) => {
    setGroups({ ...groups, [key]: { ...(groups[key] || {}), label, emails: groups[key]?.emails || [] } });
    setGroupsDirty(true);
  };
  const addGroup = () => {
    const k = window.prompt('Clave del grupo (sin espacios, ej: "operaciones"):', '');
    if (!k) return;
    const key = k.trim().toLowerCase().replace(/\s+/g, '_');
    if (groups[key]) { toast.warning('Ese grupo ya existe'); return; }
    setGroups({ ...groups, [key]: { label: key, emails: [] } });
    setGroupsDirty(true);
  };
  const removeGroup = (key) => {
    if (!window.confirm(`¿Eliminar el grupo "${groups[key]?.label || key}"?`)) return;
    const next = { ...groups };
    delete next[key];
    setGroups(next);
    setGroupsDirty(true);
    // También limpiarlo de cfg si estaba seleccionado
    setCfg({
      ...cfg,
      groups_to: (cfg.groups_to || []).filter((k) => k !== key),
      groups_cc: (cfg.groups_cc || []).filter((k) => k !== key),
    });
    setCfgDirty(true);
  };
  const testNow = async () => {
    try {
      const res = await api.post('/contabilidad/duplicados/test');
      const { sent, total_duplicados, reason } = res.data || {};
      if (sent) toast.success(`Email enviado · ${total_duplicados} duplicados`);
      else toast.warning(`No se envió · ${reason || 'sin detalles'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando test');
    }
  };

  const kpis = useMemo(() => ({
    pendientes: pending.total_pendientes || 0,
    riesgoAlto: (pending.duplicados || []).filter((d) => d.riesgo === 'alto').length,
    montoRiesgo: pending.monto_riesgo_alto || 0,
    decisiones: decisiones.length,
    historicos: historicos.total || 0,
    aRecuperar: historicos.monto_a_recuperar || 0,
  }), [pending, decisiones, historicos]);

  return (
    <div className="p-4 space-y-4" data-testid="duplicados-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSearch className="w-6 h-6 text-red-600" />
            Facturas Duplicadas
          </h1>
          <p className="text-sm text-muted-foreground">
            Detecta facturas que probablemente fueron ingresadas dos veces. Decide caso por caso antes de la próxima nómina.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} data-testid="btn-reload">
          <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Pendientes de revisar" value={kpis.pendientes} icon={<AlertTriangle className="w-4 h-4 text-red-600" />} testid="kpi-pendientes" />
        <Kpi label="Riesgo ALTO" value={kpis.riesgoAlto} extra={`$${fmtCLP(kpis.montoRiesgo)}`} icon={<span>🚨</span>} testid="kpi-alto" />
        <Kpi label="Decisiones tomadas" value={kpis.decisiones} icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} testid="kpi-decisiones" />
        <Kpi label="Históricos pagados" value={kpis.historicos} extra={`A recuperar $${fmtCLP(kpis.aRecuperar)}`} icon={<History className="w-4 h-4 text-blue-600" />} testid="kpi-historicos" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pendientes" data-testid="tab-pendientes">Pendientes ({kpis.pendientes})</TabsTrigger>
          <TabsTrigger value="resueltos" data-testid="tab-resueltos">Resueltos ({kpis.decisiones})</TabsTrigger>
          <TabsTrigger value="historicos" data-testid="tab-historicos">Históricos pagados ({kpis.historicos})</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><Settings className="w-3.5 h-3.5 mr-1" /> Configuración</TabsTrigger>
        </TabsList>

        <TabsContent value="pendientes" className="mt-3">
          {loading ? <TableSkeleton rows={4} cols={6} /> : (
            (pending.duplicados || []).length === 0 ? (
              <Card><CardContent className="py-12 text-center" data-testid="empty-pendientes">
                <ShieldCheck className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                <p className="text-lg font-semibold">¡Sin duplicados pendientes!</p>
                <p className="text-sm text-muted-foreground">Todo limpio o ya decidido. El detector seguirá vigilando diariamente.</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {(pending.duplicados || []).map((dup) => (
                  <DupCard key={dup.pair_id} dup={dup} onDecidir={openDecision} />
                ))}
              </div>
            )
          )}
        </TabsContent>

        <TabsContent value="resueltos" className="mt-3">
          {decisiones.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">Sin decisiones tomadas todavía.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {decisiones.map((d) => (
                <Card key={d.pair_id} data-testid={`decision-${d.pair_id}`}>
                  <CardContent className="p-3 text-sm flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <div className="font-mono text-xs">{d.pair_id}</div>
                      <div className="mt-1">
                        <span className={`px-2 py-0.5 text-[10px] rounded border ${d.decision === 'duplicado' ? 'bg-red-50 text-red-700 border-red-300' : 'bg-emerald-50 text-emerald-700 border-emerald-300'}`}>
                          {d.decision === 'duplicado' ? '🗑️ DUPLICADO' : '✓ LEGÍTIMAS'}
                        </span>
                        {d.decision === 'duplicado' && d.eliminar_alegra_id && (
                          <span className="ml-2 text-xs text-muted-foreground">Eliminó alegra_id={d.eliminar_alegra_id}</span>
                        )}
                      </div>
                      {d.observacion && <div className="mt-1 text-xs italic">"{d.observacion}"</div>}
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {d.decidido_por} · {(d.decidido_en || '').slice(0, 16).replace('T', ' ')}
                        {d.eliminacion?.ok === false && d.eliminacion?.razon && ` · ${d.eliminacion.razon}`}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="historicos" className="mt-3">
          {(historicos.historicos || []).length === 0 ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">Sin históricos registrados.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              <Card className="bg-amber-50 border-amber-300"><CardContent className="p-3 text-sm">
                💸 Total identificado a reclamar a proveedores: <b>${fmtCLP(historicos.monto_a_recuperar)}</b>
              </CardContent></Card>
              {(historicos.historicos || []).map((h, idx) => (
                <Card key={idx} data-testid={`historico-${idx}`}>
                  <CardContent className="p-3 text-sm">
                    <div className="font-semibold">{h.proveedor} · ${fmtCLP(h.monto_unitario)}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Folios: <span className="font-mono">{(h.folios || []).join(' ↔ ')}</span> · Pagado 2 veces:
                      ${fmtCLP(h.monto_total_pagado)} · Fechas: {(h.fechas_emision || []).join(' / ')}
                    </div>
                    {h.observacion && <div className="text-xs italic mt-1">{h.observacion}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="config" className="mt-3">
          <Card><CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base">Habilitar detector diario</Label>
              <Switch checked={cfg.enabled} onCheckedChange={(v) => { setCfg({ ...cfg, enabled: v }); setCfgDirty(true); }} data-testid="cfg-enabled" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hora (zona Chile)</Label>
                <Input value={cfg.hora} onChange={(e) => { setCfg({ ...cfg, hora: e.target.value }); setCfgDirty(true); }} placeholder="08:30" data-testid="cfg-hora" />
              </div>
              <div>
                <Label>Ventana días (regla R2)</Label>
                <Input type="number" value={cfg.dias_ventana} onChange={(e) => { setCfg({ ...cfg, dias_ventana: parseInt(e.target.value) || 15 }); setCfgDirty(true); }} data-testid="cfg-dias" />
              </div>
            </div>
            <div>
              <Label>Para (emails individuales, coma separados)</Label>
              <Input value={(cfg.to || []).join(', ')} onChange={(e) => { setCfg({ ...cfg, to: e.target.value.split(',').map((s) => s.trim()) }); setCfgDirty(true); }} placeholder="carlos@chirinosteel.com, contabilidad@…" data-testid="cfg-to" />
            </div>
            <div>
              <Label>CC (emails individuales, opcional)</Label>
              <Input value={(cfg.cc || []).join(', ')} onChange={(e) => { setCfg({ ...cfg, cc: e.target.value.split(',').map((s) => s.trim()) }); setCfgDirty(true); }} data-testid="cfg-cc" />
            </div>

            {/* Grupos reutilizables */}
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Grupos reutilizables</Label>
                <Button size="sm" variant="outline" onClick={addGroup} data-testid="btn-add-group">
                  + Nuevo grupo
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Marca un grupo en <b>Para</b> o <b>CC</b> abajo y se enviará a todos sus miembros. Edita los emails de cada grupo aquí mismo.
              </p>
              <div className="space-y-2">
                {Object.entries(groups).map(([key, g]) => (
                  <div key={key} className="p-2 border rounded bg-muted/30 space-y-1" data-testid={`group-${key}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input value={g.label || ''} onChange={(e) => updateGroupLabel(key, e.target.value)} className="max-w-[200px] h-8" data-testid={`group-label-${key}`} />
                      <span className="text-[10px] font-mono text-muted-foreground">key: {key}</span>
                      <span className="text-[10px] text-muted-foreground">· {(g.emails || []).length} email(s)</span>
                      <Button size="sm" variant="ghost" className="ml-auto h-7 text-red-600" onClick={() => removeGroup(key)} data-testid={`btn-remove-group-${key}`}>
                        Eliminar
                      </Button>
                    </div>
                    <Input value={(g.emails || []).join(', ')} onChange={(e) => updateGroupEmails(key, e.target.value)} placeholder="email1@…, email2@…" className="text-xs" data-testid={`group-emails-${key}`} />
                  </div>
                ))}
                {Object.keys(groups).length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Aún no hay grupos. Crea uno con "+ Nuevo grupo".</p>
                )}
              </div>
              <Button size="sm" onClick={saveGroups} disabled={!groupsDirty} className="mt-2" data-testid="btn-save-groups">
                Guardar grupos
              </Button>
            </div>

            <div className="pt-3 border-t">
              <Label>Grupos en "Para"</Label>
              <div className="flex gap-2 flex-wrap mt-1" data-testid="cfg-groups-to">
                {Object.entries(groups).map(([key, g]) => {
                  const on = (cfg.groups_to || []).includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleGroupOn('groups_to', key)}
                      className={`text-xs px-2 py-1 rounded border transition ${on ? 'bg-emerald-100 border-emerald-400 text-emerald-800' : 'bg-white hover:bg-muted'}`}
                      data-testid={`chip-to-${key}`}
                    >
                      {on ? '✓ ' : '+ '}{g.label} ({(g.emails || []).length})
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Grupos en "CC"</Label>
              <div className="flex gap-2 flex-wrap mt-1" data-testid="cfg-groups-cc">
                {Object.entries(groups).map(([key, g]) => {
                  const on = (cfg.groups_cc || []).includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleGroupOn('groups_cc', key)}
                      className={`text-xs px-2 py-1 rounded border transition ${on ? 'bg-blue-100 border-blue-400 text-blue-800' : 'bg-white hover:bg-muted'}`}
                      data-testid={`chip-cc-${key}`}
                    >
                      {on ? '✓ ' : '+ '}{g.label} ({(g.emails || []).length})
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2 flex-wrap">
              <Button onClick={saveCfg} disabled={!cfgDirty} data-testid="cfg-save">
                <Settings className="w-4 h-4 mr-1" /> Guardar
              </Button>
              <Button variant="outline" onClick={testNow} data-testid="cfg-test">
                <Mail className="w-4 h-4 mr-1" /> Enviar email de prueba ahora
              </Button>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Decisión */}
      <Dialog open={!!decDlg} onOpenChange={(o) => !o && setDecDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="dlg-decision">
          <DialogHeader>
            <DialogTitle>
              {decDlg?.decision === 'duplicado' ? '🗑️ Confirmar duplicado' : '✓ Marcar como legítimas'}
            </DialogTitle>
            <DialogDescription>
              {decDlg?.decision === 'duplicado'
                ? 'Esta acción ELIMINA la factura seleccionada de Flujo de Caja. La decisión queda registrada con tu nombre.'
                : 'Confirmas que ambas facturas son legítimas (mismo monto pero distintas). No se elimina nada.'}
            </DialogDescription>
          </DialogHeader>
          {decDlg?.decision === 'duplicado' && decDlg?.dup?.bills?.length === 2 && (
            <div className="space-y-2">
              <Label>¿Cuál factura es la duplicada (a eliminar)?</Label>
              {decDlg.dup.bills.map((b) => (
                <label key={b.alegra_id} className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-muted">
                  <input type="radio" name="elim" value={b.alegra_id}
                    checked={eliminarId === b.alegra_id}
                    onChange={(e) => setEliminarId(e.target.value)}
                    data-testid={`radio-elim-${b.alegra_id}`} />
                  <div className="text-xs">
                    <div className="font-mono">{b.numero} · alegra_id={b.alegra_id}</div>
                    <div className="text-muted-foreground">
                      {b.fecha} · venc {b.fecha_vencimiento} · ${fmtCLP(b.total)} · saldo ${fmtCLP(b.saldo || 0)}
                      {(b.saldo || 0) === 0 && <span className="ml-1 text-amber-600">(ya pagada — solo se registra decisión, no se elimina de BD)</span>}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
          <div>
            <Label>Observación / motivo (recomendado)</Label>
            <Textarea value={obsDec} onChange={(e) => setObsDec(e.target.value)} rows={2}
              placeholder={decDlg?.decision === 'duplicado'
                ? 'Ej: revisado con Juan (compras), proveedor confirma duplicado.'
                : 'Ej: confirmado con contabilidad — son 2 facturas distintas del mismo proveedor (servicios mensuales).'}
              data-testid="textarea-obs" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecDlg(null)}>Cancelar</Button>
            <Button
              onClick={confirmDecision}
              disabled={procesando || (decDlg?.decision === 'duplicado' && !eliminarId)}
              variant={decDlg?.decision === 'duplicado' ? 'destructive' : 'default'}
              data-testid="btn-confirm-decision"
            >
              {procesando && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {decDlg?.decision === 'duplicado' ? <Trash2 className="w-4 h-4 mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {decDlg?.decision === 'duplicado' ? 'Confirmar y eliminar' : 'Confirmar legítimas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, extra, icon, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span>{icon}</span>
        </div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {extra && <div className="text-xs text-muted-foreground tabular-nums">{extra}</div>}
      </CardContent>
    </Card>
  );
}

function DupCard({ dup, onDecidir }) {
  const meta = RIESGO_META[dup.riesgo] || RIESGO_META.bajo;
  const [a, b] = dup.bills || [];
  return (
    <Card data-testid={`dup-${dup.pair_id}`}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded border ${meta.cls}`}>{meta.icon} {meta.txt}</span>
              <span className="font-semibold">{dup.proveedor}</span>
              <span className="tabular-nums text-purple-700 font-mono">${fmtCLP(dup.monto_unitario)}</span>
            </div>
            <div className="mt-1 text-xs italic text-muted-foreground">{dup.motivo}</div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <BillRow b={a} />
              <BillRow b={b} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Button size="sm" variant="destructive" onClick={() => onDecidir(dup, 'duplicado')} data-testid={`btn-dup-${dup.pair_id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Es duplicado
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDecidir(dup, 'legitimo')} data-testid={`btn-leg-${dup.pair_id}`}>
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Son legítimas
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BillRow({ b }) {
  if (!b) return null;
  const pendiente = (b.saldo || 0) > 0;
  return (
    <div className={`p-2 rounded border ${pendiente ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
      <div className="font-mono font-semibold">{b.numero} <span className="font-normal text-muted-foreground">· id {b.alegra_id}</span></div>
      <div className="text-[11px] text-muted-foreground">
        emitida {b.fecha || '?'} · venc {b.fecha_vencimiento || '?'}
      </div>
      <div className="text-[11px]">
        Total ${fmtCLP(b.total)} · {pendiente ? <span className="text-red-700 font-semibold">Pendiente ${fmtCLP(b.saldo)}</span> : <span className="text-emerald-700">Pagada</span>}
      </div>
    </div>
  );
}
